// @ts-nocheck
import { FORTA_BLACK, FORTA_ERROR } from '../../../../utils/colors';
import { INPUT_LABEL_NUDGE } from '../constants';

import type { SxProps, Theme } from '@mui/material';

type OutlinedState = { error: boolean; disabled: boolean | undefined };

export type FCheckboxGroupAriaIds = {
  labelId: string;
  helperTextId: string;
};

type BuildCheckboxGroupLayoutSxParams = {
  row: boolean;
  checkboxGroupSx: SxProps<Theme> | undefined;
  formControlLabelRowSx: SxProps<Theme>;
  formControlLabelVerticalSx: SxProps<Theme>;
};

function resolveCheckboxGroupBaseId(idProp: string | undefined, generatedId: string): string {
  return idProp != null && idProp !== '' ? idProp : generatedId;
}

export function buildCheckboxGroupAriaIds(
  idProp: string | undefined,
  generatedId: string,
): FCheckboxGroupAriaIds {
  const baseId = resolveCheckboxGroupBaseId(idProp, generatedId);
  return {
    labelId: `fcheckbox-group-label-${baseId}`,
    helperTextId: `fcheckbox-group-helper-${baseId}`,
  };
}

export function buildCheckboxGroupLayoutSx({
  row,
  checkboxGroupSx,
  formControlLabelRowSx,
  formControlLabelVerticalSx,
}: BuildCheckboxGroupLayoutSxParams): SxProps<Theme> {
  const base: SxProps<Theme> = (theme) => ({
    width: '100%',
    minWidth: 0,
    minHeight: 0,
    p: 1,
    margin: 0,
    boxSizing: 'border-box',
    columnGap: row ? theme.spacing(0.75) : 0,
    rowGap: row ? 0 : theme.spacing(0.25),
    '& .MuiFormControlLabel-label': { marginLeft: 0, marginRight: 0, paddingLeft: 0 },
    '& .MuiFormControlLabel-root': row ? formControlLabelRowSx : formControlLabelVerticalSx,
  });

  if (checkboxGroupSx == null) {
    return base;
  }

  return [base, checkboxGroupSx];
}

export function getCheckboxGroupOutlinedBoxSx(state: OutlinedState): SxProps<Theme> {
  return (theme) => {
    const border = state.error ? FORTA_ERROR : FORTA_BLACK;
    return {
      px: 1,
      py: 0.5,
      position: 'relative',
      boxSizing: 'border-box',
      border: `1px solid ${border}`,
      borderRadius: 1.5,
      transition: theme.transitions.create('border-color', {
        duration: theme.transitions.duration.shorter,
      }),
      '&:hover': {
        border: `1px solid ${border}`,
      },
      ...((state.disabled ?? false)
        ? { opacity: theme.palette.action.disabledOpacity, pointerEvents: 'none' }
        : {
            '&:focus-within': {
              border: `1px solid ${border}`,
            },
          }),
    };
  };
}

export function mergeCheckboxGroupBorderSx(
  borderSx: SxProps<Theme>,
  userSx: SxProps<Theme> | undefined,
): SxProps<Theme> {
  if (userSx == null) {
    return borderSx;
  }

  return [borderSx, userSx];
}

export function mergeSxProps(base: SxProps<Theme>, sx: SxProps<Theme> | undefined): SxProps<Theme> {
  if (sx == null) {
    return base;
  }

  return [base, ...(Array.isArray(sx) ? sx : [sx])];
}

export function buildInputLabelSx(inputLabelUserSx: SxProps<Theme> | undefined): SxProps<Theme> {
  return [
    (theme) => ({
      position: 'absolute',
      top: 0,
      left: '14px',
      zIndex: 1,
      transform: `translate(0, ${INPUT_LABEL_NUDGE}px) scale(0.75)`,
      transformOrigin: 'top left',
      backgroundColor: theme.palette.background.paper,
      maxWidth: 'calc(100% - 24px)',
      px: 1,
      pointerEvents: 'none',
      lineHeight: 1.4375,
      color: FORTA_BLACK,
    }),
    ...(inputLabelUserSx != null
      ? Array.isArray(inputLabelUserSx)
        ? inputLabelUserSx
        : [inputLabelUserSx]
      : []),
  ];
}
